<script lang="ts">
  // Cyberspace "dive" boot screen. Covers the void while the Pixi world bakes its
  // terrain, then fades to reveal the live map. The portal ring IS the progress
  // bar: a determinate arc fills with cyan as real assets decode, the percent
  // reads in its core, and the destination area names what you're entering.
  // Frozen to a static frame under prefers-reduced-motion.
  import { fade } from "svelte/transition";
  import { expoOut } from "svelte/easing";

  let {
    progress = 0,
    phase = "",
    area = "",
  }: { progress?: number; phase?: string; area?: string } = $props();

  const reduced =
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Chase the real (chunky) load progress so the ring fills like flowing data
  // instead of snapping between tile batches. The renderer reports progress in a
  // burst of small steps; mirror the latest into a plain target, then ease toward
  // it from ONE persistent rAF loop. (Re-targeting inside the loop, rather than
  // restarting the loop on every report, is what keeps the animation from being
  // starved when reports arrive faster than one per frame.)
  let display = $state(0);
  let target = 0;
  $effect(() => {
    target = Math.max(0, Math.min(1, progress));
    if (reduced) display = target;
  });
  $effect(() => {
    if (reduced) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number): void => {
      const dt = Math.min(64, now - last);
      last = now;
      display += (target - display) * (1 - Math.exp(-dt / 140));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  const pct = $derived(Math.round(display * 100));

  // Progress-arc geometry: a circle of radius R drawn clockwise from 12 o'clock.
  const R = 86;
  const CIRC = 2 * Math.PI * R;
  const offset = $derived(CIRC * (1 - display));
  // Glowing "write head" riding the leading edge of the arc.
  const ang = $derived(((-90 + 360 * display) * Math.PI) / 180);
  const hx = $derived(100 + R * Math.cos(ang));
  const hy = $derived(100 + R * Math.sin(ang));
</script>

{#snippet bracket(pos: string)}
  <svg
    viewBox="0 0 44 44"
    class="pointer-events-none absolute size-9 text-cyan/30 sm:size-11 {pos}"
    fill="none"
    stroke="currentColor"
    stroke-width="1.25"
    aria-hidden="true"
  >
    <path d="M2 16 V2 H16" />
    <path d="M2 24 V2 H10" opacity="0.5" />
  </svg>
{/snippet}

<div
  class="loader fixed inset-0 z-50 overflow-hidden bg-void"
  out:fade={{ duration: reduced ? 120 : 460, easing: expoOut }}
  role="status"
  aria-busy="true"
>
  <!-- Ambient cyberspace field: drifting grid, a portal glow blooming from the
       centre, an inward vignette, and a slow scan sweep. -->
  <div class="grid-drift grid-tex absolute -inset-[12%]" aria-hidden="true"></div>
  <div
    class="absolute inset-0"
    aria-hidden="true"
    style="background: radial-gradient(circle at 50% 47%, color-mix(in oklab, var(--color-cyan) 13%, transparent), transparent 46%)"
  ></div>
  <div
    class="absolute inset-0"
    aria-hidden="true"
    style="background: radial-gradient(ellipse at center, transparent 32%, var(--color-void) 82%)"
  ></div>
  <div class="scan absolute inset-y-0 left-0" aria-hidden="true"></div>

  {@render bracket("left-5 top-5 sm:left-7 sm:top-7")}
  {@render bracket("right-5 top-5 rotate-90 sm:right-7 sm:top-7")}
  {@render bracket("bottom-5 right-5 rotate-180 sm:bottom-7 sm:right-7")}
  {@render bracket("bottom-5 left-5 -rotate-90 sm:bottom-7 sm:left-7")}

  <!-- Top gateway readout. -->
  <div
    class="loader-readout absolute inset-x-0 top-6 flex justify-center font-display text-xs uppercase tracking-[0.42em] text-ink-dim/75"
  >
    <span class="loader-readout-label">Network&nbsp;Dive</span>
  </div>

  <!-- Hero column: portal ring → destination → status. -->
  <div class="loader-hero absolute inset-0 flex flex-col items-center justify-center gap-7 px-6">
    <div class="loader-ring relative grid size-[clamp(212px,40vw,292px)] place-items-center">
      <!-- Soft breathing halo behind the ring. -->
      <div
        class="absolute inset-[16%] rounded-full animate-pulse-soft"
        style="background: radial-gradient(circle, color-mix(in oklab, var(--color-cyan) 26%, transparent), transparent 70%)"
        aria-hidden="true"
      ></div>

      <!-- Decorative rotating rings + tick ring. -->
      <svg
        viewBox="0 0 200 200"
        class="animate-ring absolute inset-0 size-full text-cyan/25"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
      >
        <circle cx="100" cy="100" r="97" stroke-width="0.5" stroke-dasharray="2 6" />
        <circle cx="100" cy="100" r="70" stroke-width="0.4" stroke-dasharray="14 8" />
        {#each Array(36) as _, i (i)}
          <line
            x1="100"
            y1="3"
            x2="100"
            y2={i % 3 === 0 ? 11 : 7}
            stroke-width="0.7"
            transform="rotate({i * 10} 100 100)"
          />
        {/each}
      </svg>

      <!-- Determinate progress arc + leading write-head. -->
      <svg viewBox="0 0 200 200" class="absolute inset-0 size-full" fill="none" aria-hidden="true">
        <circle cx="100" cy="100" r={R} stroke="var(--color-cyan)" stroke-opacity="0.13" stroke-width="2.5" />
        <circle
          cx="100"
          cy="100"
          r={R}
          stroke="var(--color-cyan)"
          stroke-width="3"
          stroke-linecap="round"
          stroke-dasharray={CIRC}
          stroke-dashoffset={offset}
          transform="rotate(-90 100 100)"
          style="filter: drop-shadow(0 0 7px color-mix(in oklab, var(--color-cyan) 80%, transparent))"
        />
        {#if display > 0.012 && display < 0.999}
          <circle
            cx={hx}
            cy={hy}
            r="3.4"
            fill="#eafcff"
            style="filter: drop-shadow(0 0 7px var(--color-cyan)) drop-shadow(0 0 3px var(--color-cyan))"
          />
        {/if}
      </svg>

      <!-- Percent core. -->
      <div class="relative text-center leading-none">
        <div class="font-display font-bold text-ink text-glow">
          <span class="text-[clamp(2.7rem,7.4vw,3.6rem)] tabular-nums">{pct}</span><span
            class="ml-0.5 align-top text-[clamp(1rem,2.4vw,1.35rem)] font-semibold text-cyan">%</span
          >
        </div>
      </div>
    </div>

    <!-- Destination + live phase readout. -->
    <div class="loader-destination flex flex-col items-center gap-3 text-center">
      <div
        class="loader-area animate-power-on max-w-[92vw] text-balance font-display text-[clamp(1.35rem,4.4vw,2.2rem)] font-bold uppercase leading-[1.05] tracking-[0.16em] text-ink"
      >
        {area || "The World"}
      </div>
      <div class="h-px w-28 bg-gradient-to-r from-transparent via-cyan/55 to-transparent"></div>
      <div
        class="flex items-center gap-2 font-display text-[0.8rem] uppercase tracking-[0.22em]"
        aria-live="polite"
      >
        <span
          class="size-1.5 shrink-0 rounded-full bg-cyan animate-pulse-soft"
          style="filter:drop-shadow(0 0 5px var(--color-cyan))"
          aria-hidden="true"
        ></span>
        <span class="text-ink-dim">{phase}<span class="ellipsis" aria-hidden="true"></span></span>
      </div>
    </div>
  </div>
</div>

<style>
  .loader-hero {
    transform: scale(var(--world-ui-scale));
    transform-origin: center;
  }
  .loader-readout-label {
    display: inline-block;
    transform: scale(var(--world-ui-scale));
    transform-origin: top center;
  }
  /* Grid drifts one cell on a loop for a sense of forward motion. */
  .grid-drift {
    opacity: 0.5;
    animation: grid-drift 22s linear infinite;
  }
  @keyframes grid-drift {
    from {
      transform: translate(0, 0);
    }
    to {
      transform: translate(22px, 22px);
    }
  }

  /* A single soft cyan band sweeping the field. */
  .scan {
    width: 38%;
    background: linear-gradient(
      90deg,
      transparent,
      color-mix(in oklab, var(--color-cyan) 9%, transparent),
      transparent
    );
    mix-blend-mode: screen;
    animation: scanx 4.8s var(--ease-out-quint) infinite;
  }
  @keyframes scanx {
    0% {
      transform: translateX(-120%);
      opacity: 0;
    }
    14% {
      opacity: 1;
    }
    86% {
      opacity: 1;
    }
    100% {
      transform: translateX(360%);
      opacity: 0;
    }
  }

  /* Animated trailing dots on the phase line. */
  .ellipsis::after {
    content: "";
    animation: dots 1.4s steps(4, end) infinite;
  }
  @keyframes dots {
    0% {
      content: "";
    }
    25% {
      content: ".";
    }
    50% {
      content: "..";
    }
    75% {
      content: "...";
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .grid-drift,
    .ellipsis::after {
      animation: none;
    }
    .scan {
      display: none;
    }
  }

  :global(.phone) .loader-hero {
    flex-direction: row;
    gap: clamp(1rem, 5vw, 2.5rem);
    padding:
      max(2.25rem, env(safe-area-inset-top))
      max(2.25rem, env(safe-area-inset-right))
      max(1rem, env(safe-area-inset-bottom))
      max(2.25rem, env(safe-area-inset-left));
  }
  :global(.phone) .loader-ring {
    width: min(44vh, 11rem);
    height: min(44vh, 11rem);
    flex: 0 0 auto;
  }
  :global(.phone) .loader-destination {
    align-items: flex-start;
    gap: 0.55rem;
    text-align: left;
  }
  :global(.phone) .loader-area {
    max-width: 48vw;
    font-size: clamp(1.15rem, 3.8vw, 1.75rem);
    letter-spacing: 0.12em;
  }
</style>
